import React, { useEffect, useState, useRef, useMemo } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { useParams, useHistory, Link as RouterLink } from 'react-router-dom';
import { DateTimePicker } from '@material-ui/pickers';
import { DateTime } from 'luxon';
import { SketchPicker } from 'react-color'
import { EditorState } from 'draft-js';
import { htmlFromEditorState, editorStateFromHtml } from "app/utils/DraftConvert";
import { cloneDeep } from 'lodash';

import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import Collapse from '@material-ui/core/Collapse';
import ButtonGroup from '@material-ui/core/ButtonGroup';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import Grow from '@material-ui/core/Grow';
import Grid from '@material-ui/core/Grid';
import Popper from '@material-ui/core/Popper';
import MenuItem from '@material-ui/core/MenuItem';
import MenuList from '@material-ui/core/MenuList';
import Alert from '@material-ui/lab/Alert';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import InputAdornment from '@material-ui/core/InputAdornment';
import OutlinedInput from '@material-ui/core/OutlinedInput';
import InputLabel from '@material-ui/core/InputLabel';
import Fab from '@material-ui/core/Fab';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListSubheader from '@material-ui/core/ListSubheader';
import ListItemText from '@material-ui/core/ListItemText';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import Popover from '@material-ui/core/Popover';
import Zoom from '@material-ui/core/Zoom';

import AddIcon from '@material-ui/icons/Add';
import SubjectIcon from '@material-ui/icons/Subject';
import ImageIcon from '@material-ui/icons/Image';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import LinkIcon from '@material-ui/icons/Link';
import DeleteIcon from '@material-ui/icons/Delete';

import SelectFileDialog from 'app/components/drive/SelectFileDialog';
import RichTextEditor from 'app/components/rte/RichTextEditor';
import RichImageEditor from 'app/components/images/RichImageEditor';
import RichLinkEditor from 'app/components/rte/RichLinkEditor';

// Local imports
import { paper } from 'app/styles/core';
import useApi from 'app/context/useApi';
import useApp from 'app/context/useApp';
import { containerFlex, containerFlexFixed, containerFlexGrow, navWidth } from 'app/styles/new-layout';
import Loading from 'app/components/loaders/Loading';

const useBlockStyles = makeStyles(theme => ({
    blockContainer: props => ({
        position: 'relative',
        borderBottom: props.active ? `1px solid #ddd` : null
    }),
    blockControls: {
        position: 'absolute',
        left: '-66px',
        width: '66px',
        top: 0,
        bottom: 0
    },
    fab: {
        backgroundColor: '#ffffff'
    }
}));

const Block = (props) => {
    const { active, handleSelect, index, handleOptions, options, onMoveUp, onMoveDown, onRemove } = props;

    const classes = useBlockStyles({ active: active });

    const controls = (
        <Grid container wrap="nowrap" direction="column" justifyContent="space-between" alignItems='center' className={classes.blockControls}>
            <Grid item>
                <Zoom in={active}>
                    <Fab
                        aria-label="Move Up"
                        size="small"
                        onClick={(e) => onMoveUp(index, e)}
                        className={classes.fab}
                        >
                        <ExpandLessIcon />
                    </Fab>
                </Zoom>
            </Grid>
            <Grid item>
                <Zoom in={active}>
                    <Fab
                        aria-label="Remove Block"
                        size="small"
                        onClick={(e) => onRemove(index, e)}
                        className={classes.fab}
                        >
                        <DeleteIcon />
                    </Fab>
                </Zoom>
            </Grid>
            <Grid item>
                <Zoom in={active}>
                    <Fab
                        aria-label="Move Down"
                        size="small"
                        onClick={(e) => onMoveDown(index, e)}
                        className={classes.fab}
                        >
                        <ExpandMoreIcon />
                    </Fab>
                </Zoom>
            </Grid>
        </Grid>
    )

    switch (options.type) {
        case 'text': return (
            <div className={classes.blockContainer} onClick={(event) => handleSelect(index, event)}>
                <RichTextEditor
                    active={active}
                    handleOptions={(name, value) => handleOptions(index, value, name)}
                    options={options}
                    editorState={options.editor}
                    setEditorState={val => handleOptions(index, val, 'editor')}
                />
                {controls}
            </div>
        );
        case 'image': return (
            <div className={classes.blockContainer} onClick={(event) => handleSelect(index, event)}>
                <RichImageEditor
                    options={options}
                    handleOptions={(name, value) => handleOptions(index, value, name)}
                    active={active}
                    type="page"
                />
                {controls}
            </div>
        )
        case 'link': return (
            <div className={classes.blockContainer} onClick={(event) => handleSelect(index, event)}>
                <RichLinkEditor
                    options={options}
                    handleOptions={(name, value) => handleOptions(index, value, name)}
                    active={active}
                />
                {controls}
            </div>
        )
    }
}

const renderDateInput = props => (
    <TextField
        variant="outlined"
        fullWidth
        className={props.className}
        onClick={props.onClick}
        id={props.id}
        value={props.value}
        disabled={props.disabled}
        label={props.label}
        helperText={props.helperText}
        {...props.inputProps}
    />
);

const useStyles = makeStyles(theme => ({
    container: {
        ...containerFlex(theme),
        padding: theme.spacing(3),
    },
    content: {
        ...containerFlexGrow(theme),
        position: 'relative',
        paddingRight: theme.spacing(3),

        [theme.breakpoints.down('md')]: {
            paddingRight: '0px',
        }
    },
    nav: {
        ...containerFlexFixed(theme),
        maxWidth: `${navWidth}px`
    },
    paper: {
        ...paper(theme),
        position: 'relative',
        marginBottom: theme.spacing(3)
    },
    email: {
        borderRadius: theme.shape.borderRadius,
        boxShadow: '0px 0px 20px -10px inset',
        minHeight: 200,
        position: 'relative',
        paddingTop: theme.spacing(3),
        paddingBottom: theme.spacing(13),
        paddingLeft: 66,
        paddingRight: 66,
        backgroundColor: '#dddddd'
    },
    emailContent: {
        maxWidth: 600,
        margin: '0 auto',
    },
    fab: {
        position: 'absolute',
        bottom: theme.spacing(3),
        right: theme.spacing(3),
    },
    buttonGroup: {
        width: '100%',
    },
    buttonExpand: {
        flexGrow: 1
    },
    marginTop: {
        marginTop: theme.spacing(3),
    },
    marginRight: {
        marginRight: theme.spacing(3),
    },
    marginBottom: {
        marginBottom: theme.spacing(3)
    },
    alert: {
        marginBottom: theme.spacing(3),
        textTransform: 'capitalize'
    },
    colorSwatch: {
        height: 32,
        width: 32,
        marginRight: theme.spacing(3),
        verticalAlign: 'middle',
        display: 'inline-block',
        borderRadius: theme.shape.borderRadius,
        cursor: 'pointer',
        border: `1px solid ${theme.palette.grey[400]}`,
    },
}));

const initialPage = {
    id: 0,
    background_image_id: 0,
    background_color: '#FFFFFF',
    group_id: 0,
    path: "",
    title: "",
    short_description: "",
    content: "",
    nav_label: "",
    nav_order: 0,
    public: 0,
    published_at: null,
    icon: 'Book'
}

const publishOptions = {
    new: ['Publish Now', 'Publish Later', 'Save as Draft'],
    draft: ['Update Draft', 'Publish Now', 'Publish Later', 'Delete Draft'],
    scheduled: ['Update Draft', 'Publish Now', 'Delete Draft'],
    published: ['Update Page', 'Save as Draft', 'Delete Page'],
}

export default function EditPage() {

    // State and constants
    const classes = useStyles();
    const api = useApi();
    const params = useParams();
    const history = useHistory();
    const anchorRef = useRef(null);
    const { app } = useApp();

    const [ page, setPage ] = useState(initialPage);
    const [ groups, setGroups ] = useState([]);
    const [ status, setStatus ] = useState('new');
    const [ backgroundImage, setBackgroundImage ] = useState(null);

    const [ blocks, setBlocks ] = useState([]);
    const [ activeBlockKey, setActiveBlockKey] = useState(0);
    const [ selectFileDialogOpen, setSelectFileDialogOpen ] = useState(false);
    const [ selectFileType, setSelectFileType ] = useState(null);

    const [ publishButton, setPublishButton ] = useState(publishOptions.new[0]);
    const [ publishOptionsOpen, setPublishOptionsOpen ] = useState(false);

    const [ colorPickerAnchorEl, setColorPickerAnchorEl ] = useState(null);
    const [ activeColor, setActiveColor ] = useState(null);
    const [ handlePickedColor, setHandlePickedColor ] = useState(null);

    const [ loading, setLoading ] = useState(false);
    const [ errors, setErrors ] = useState({});

    const [ newBlockAnchorEl, setNewBlockAnchorEl] = useState(null);

    // Getter function
    const getPage = () => {

        setLoading(true);

        api.get(`/admin/page/${params.id}`)
            .then(res => {

                const newPage = res.page;

                if (newPage.public === null) newPage.public = 'null'

                setPage(res.page);

                handleBlocks(res.page.blocks);

                if (res.page.background_image) {
                    setBackgroundImage(res.page.background_image);
                }

                setStatus(res.page.status);
            }).finally(() => setLoading(false))
    };

    const handleBlocks = newBlocks => {
        newBlocks.forEach(b => {
            if (b.options.type === 'text') {
                b.options.editor = editorStateFromHtml(b.options.content);
            }
        })

        setBlocks(newBlocks)
    }

    const getGroups = () => api.get('/admin/group').then(res => setGroups(res.groups));

    useEffect(() => {
        if (params.id > 0) getPage();
        getGroups();
    }, []);

    useEffect(() => {
        setPublishButton(publishOptions[status][0]);
    }, [status]);

    // Handlers --------------------------------

    const handleChange = name => event => {
        let value;

        if (DateTime.isDateTime(event) || event === null) {
            value = event;
        } else {
            const target = event.target;
            value = (target.type === 'checkbox' ? target.checked : target.value);
        }

        removeError(name);

        setPage({ ...page, [name]: value });
    }

    const handleBackgroundColor = (color) => {
        if (color && color.hex) {
            setPage({ ...page, background_color: color.hex });
            setActiveColor(color.hex);
        }
    }

    const handleToggle = () => {
        setPublishOptionsOpen(!publishOptionsOpen);
    }

    const handleClose = (event) => {
        if (anchorRef.current && anchorRef.current.contains(event.target)) return;
        setPublishOptionsOpen(false);
    };

    const handlePublishOption = option => {
        setPublishButton(option);
        setPublishOptionsOpen(false);
    }

    const addBlock = (type, event, file = null) => {

        event?.stopPropagation();

        removeError('blocks');

        const newBlocks = [...blocks];

        const newBlock = {
            id: `new-${blocks.length}`,
            options: {
                type: type,
                background_color: '#FAFAFA',
            }
        };

        switch (type) {
            case 'text':
                newBlock.options.content = null;
                newBlock.options.editor = EditorState.createEmpty();
                newBlock.options.padding_top = 24;
                newBlock.options.padding_right = 24;
                newBlock.options.padding_left = 24;
                newBlock.options.padding_bottom = 24;
                break;
            case 'image':
                newBlock.options.image_align = 'center';
                newBlock.options.image_width_type = 'percent';
                newBlock.options.image_width = 100;
                newBlock.options.content = file;
                newBlock.options.padding_top = 0;
                newBlock.options.padding_right = 0;
                newBlock.options.padding_left = 0;
                newBlock.options.padding_bottom = 0;
                break;
            case 'link':
                newBlock.options.link_type = 'link';
                newBlock.options.content = "";
                newBlock.options.padding_top = 24;
                newBlock.options.padding_right = 24;
                newBlock.options.padding_left = 24;
                newBlock.options.padding_bottom = 24;
                newBlock.options.button_text = 'Button Text';
                newBlock.options.button_text_color = '#ffffff';
                newBlock.options.button_color = app.club.branding.primary_color;
                newBlock.options.button_radius = 4;
                newBlock.options.button_align = 'center';
                newBlock.options.button_padding_width = 24;
                newBlock.options.button_padding_height = 8;
                newBlock.options.target = true;
                break;
        }

        newBlocks.push(newBlock);

        setBlocks(newBlocks)
        setActiveBlockKey(blocks.length);
        closeNewBlockDialog();
    }

    const handleBlockOptions = (i, value, name) => {

        const newBlocks = [...blocks]

        newBlocks[i].options[name] = value;

        setBlocks(newBlocks)
    };

    const handleMoveUp = (key, event) => {
        event?.stopPropagation();

        if (key > 0) {
            const previousKey = key - 1;
            const newBlocks = [...blocks];
            const block = newBlocks.splice(key, 1)[0];

            newBlocks.splice(previousKey, 0, block);

            setBlocks(newBlocks);
            setActiveBlockKey(previousKey);
        }
    }

    const handleMoveDown = (key, event) => {
        event?.stopPropagation();

        if (key < (blocks.length - 1)) {
            const nextKey = key + 1;
            const newBlocks = [...blocks];
            const block = newBlocks.splice(key, 1)[0];

            newBlocks.splice(nextKey, 0, block);

            setBlocks(newBlocks);
            setActiveBlockKey(nextKey);
        }
    }

    const handleRemoveBlock = (key, event) => {
        event?.stopPropagation();

        const newBlocks = [...blocks];

        newBlocks.splice(key,1);

        setBlocks(newBlocks);
    }

    const handleActiveBlockKey = (key, e) => {
        e.stopPropagation();

        // Check if image block is still without an image
        if (blocks[activeBlockKey]?.options.type === 'image' && blocks[activeBlockKey]?.options.content == 0 && key !== activeBlockKey) {
            handleRemoveBlock(activeBlockKey);
        }

        setActiveBlockKey(key);
    }

    // Posters ----------------------------------------

    const postPage = () => {

        setLoading(true);

        if (publishButton === 'Delete Page' || publishButton === 'Delete Draft') {
            api.delete(`/admin/page/${params.id}`)
                .then(() => history.push('/admin/pages'))
                .catch((error) => {
                    setLoading(false);
                    if (error?.response?.data?.errors) {
                        setErrors(error.response.data.errors)
                    }
                })
        } else {
            const data = {
                ...page,
                blocks: cloneDeep(blocks),
                publish: publishButton
            }
    
            if (data.public === 'null') data.public = null;
            if (DateTime.isDateTime(page.published_at)) data.published_at = page.published_at.toSeconds();
    
            data.blocks.forEach(block => {
                if (block.options.type === 'text') {
                    block.options.content = htmlFromEditorState(block.options.editor);
                    delete block.options.editor;
                }
            });
    
            api.post(`/admin/page/${params.id}`, data)
                .then(() => history.push('/admin/pages'))
                .catch((error) => {
                    setLoading(false);
                    if (error?.response?.data?.errors) {
                        setErrors(error.response.data.errors)
                    }
                })
        }
    };

    const isError = field => {
        if (errors[field]) return true;
        return false;
    }

    const getErrorMessage = field => {
        if (isError(field)) return errors[field];
        return "";
    }

    const getPathMessage = () => {
        if (isError('path')) return errors.path;
        return "Url path to get to the page.";
    }

    const removeError = field => {
        const newErrors = {...errors};

        delete newErrors[field];

        setErrors(newErrors);
    }

    // Dialogs....................

    // Select File Dialog Functions

    const openSelectFileDialog = (type = null) => {
        setSelectFileDialogOpen(true);
        setSelectFileType(type);
    }

    const closeSelectFileDialog = () => {
        setSelectFileDialogOpen(false);
    }

    const closeSelectFileDialogAndAddBlock = (file, event) => {
        closeSelectFileDialog();

        switch (selectFileType) {
            case 'image': 
                addBlock('image', event, file);
                break;
        }

        setSelectFileType(null);
    }

    // Color Picker Popover....................

    const openColorPicker = (event, color, handler) => {
        setColorPickerAnchorEl(event.currentTarget);
        setActiveColor(color);
        setHandlePickedColor(() => handler);
    }

    const closeColorPicker= () => {
        setColorPickerAnchorEl(null);
    }

    // New Block Popover....................

    const openNewBlockDialog = (event) => {
        setNewBlockAnchorEl(event.currentTarget);
    }

    const closeNewBlockDialog = () => {
        setNewBlockAnchorEl(null);
    }

    const publishText = useMemo(() => {

        if (page.published_at && status === 'published') {
            return 'Published On: ' + DateTime.fromISO(page.published_at).toLocaleString(DateTime.DATETIME_MED);
        }
        if (page.published_at && status === 'scheduled') {
            return 'Scheduled For: ' + DateTime.fromISO(page.published_at).toLocaleString(DateTime.DATETIME_MED);
        }

        return null;
    }, [status])

    const severity = useMemo(() => {
        switch (status) {
            case 'published': return 'success';
            case 'scheduled': return 'warning';
            case 'draft':
            default: return 'info'
        }
    }, [status]);

    return (
        <div className={classes.container} onClick={(e) => handleActiveBlockKey(null, e)}>
            <div className={classes.content}>
                <Paper className={classes.paper}>
                    <Loading isLoading={loading}/>
                    <Grid container alignItems="center" spacing={3}>
                        <Grid item>
                            <Typography variant="h5" component="h2">{page.title.length ? page.title : "New Page"}</Typography>
                        </Grid>
                    </Grid>
                    <TextField
                        variant='outlined'
                        label='Title'
                        value={page.title}
                        id="input-title"
                        onChange={handleChange('title')}
                        fullWidth
                        margin="normal"
                        error={isError('title')}
                        helperText={getErrorMessage('title')}
                        className={classes.marginTop}
                        required
                        disabled={page.created_by_system}
                    />
                    <FormControl 
                        variant="outlined" 
                        error={isError('path')} 
                        fullWidth 
                        className={classes.marginTop} 
                        required
                        disabled={page.created_by_system}
                        >
                        <InputLabel htmlFor="input-path">Url Path</InputLabel>
                        <OutlinedInput
                            id="input-path"
                            value={page.path}
                            onChange={handleChange('path')}
                            startAdornment={<InputAdornment>{app.club.url}/</InputAdornment>}
                            aria-describedby="path-helper-text"
                            label='Url Path'
                            placeholder={(page.title && page.title != '') ? page.title.replace(/ /g,".").toLowerCase() : "path-to-page"}
                        />
                        <FormHelperText id="path-helper-text">{getPathMessage()}</FormHelperText>
                    </FormControl>
                    <TextField
                        label="Short Description"
                        multiline
                        rows="4"
                        value={page.short_description}
                        onChange={handleChange('short_description')}
                        fullWidth
                        error={isError('short_description')}
                        helperText={getErrorMessage('short_description')}
                        required
                        variant="outlined"
                        className={classes.marginTop}
                    />
                    <Typography variant='subtitle2' gutterBottom color="textSecondary" className={classes.marginTop}>
                        Page Content:
                    </Typography>
                    <div className={classes.email} style={{ backgroundColor: page.background_color }}>
                        <div className={classes.emailContent} >
                            {isError('blocks') &&
                                <Alert severity="error">{getErrorMessage('blocks')}</Alert>
                            }
                            {blocks.map((block, i) => (
                                <Block
                                    key={block.id}
                                    index={i}
                                    active={(activeBlockKey === i)}
                                    options={block.options}
                                    handleSelect={handleActiveBlockKey}
                                    handleOptions={handleBlockOptions}
                                    onMoveUp={handleMoveUp}
                                    onMoveDown={handleMoveDown}
                                    onRemove={handleRemoveBlock}
                                />
                            ))}
                            <Fab
                                aria-label="New Block"
                                color="primary"
                                className={classes.fab}
                                onClick={openNewBlockDialog}
                                >
                                <AddIcon />
                            </Fab>
                        </div>
                    </div>
                    <Popover
                        open={Boolean(newBlockAnchorEl)}
                        anchorEl={newBlockAnchorEl}
                        onClose={closeNewBlockDialog}
                        anchorOrigin={{
                            vertical: 'top',
                            horizontal: 'left',
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'right',
                        }}
                        >
                        <List
                            subheader={
                                <ListSubheader component="div" id="nested-list-subheader">
                                    Select a block to add to your page
                                </ListSubheader>
                            }
                            className={classes.root}
                            >
                            <ListItem button onClick={(event) => addBlock('text', event)}>
                                <ListItemIcon>
                                    <SubjectIcon />
                                </ListItemIcon>
                                <ListItemText primary="Text" />
                            </ListItem>
                            <ListItem button onClick={() => openSelectFileDialog("image")}>
                                <ListItemIcon>
                                    <ImageIcon />
                                </ListItemIcon>
                                <ListItemText primary="Image" />
                            </ListItem>
                            <ListItem button onClick={(event) => addBlock('link', event)}>
                                <ListItemIcon>
                                    <LinkIcon />
                                </ListItemIcon>
                                <ListItemText primary="Link" />
                            </ListItem>
                        </List>
                    </Popover>
                </Paper>
            </div>
            <div className={classes.nav}>
                <Paper className={classes.paper}>
                    <Typography variant="h6" className={classes.marginBottom}>
                        Page Options
                    </Typography>
                    <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                        Page Background Color
                    </Typography>
                    <div className={classes.marginBottom}>
                        <span
                            className={classes.colorSwatch}
                            style={{ backgroundColor: page.background_color }}
                            onClick={(event) => openColorPicker(event, page.background_color, handleBackgroundColor)}
                        />
                        <Typography variant="body2" color="textSecondary" component="span">{page.background_color}</Typography>
                    </div>
                    <Typography variant='subtitle2' gutterBottom color="textSecondary">
                        Select the visibility of this page.
                    </Typography>
                    <TextField
                        id="public-select"
                        select
                        value={page.public}
                        onChange={handleChange('public')}
                        helperText={getErrorMessage('public')}
                        variant="outlined"
                        fullWidth
                        color="primary"
                        className={classes.marginBottom}
                        disabled={page.created_by_system}
                        >
                        <MenuItem value={'null'}>
                            Viewable by the public and authenticated users
                        </MenuItem>
                        <MenuItem value={0}>
                            Viewable by authenticated users only
                        </MenuItem>
                        <MenuItem value={1}>
                            Viewable by the public only
                        </MenuItem>
                    </TextField>
                    {(groups.length) &&
                        <>
                            <Typography variant='subtitle2' gutterBottom color="textSecondary">
                                Select the group of users that can view this page.
                            </Typography>
                            <TextField
                                id="group-select"
                                select
                                value={page.group_id}
                                onChange={handleChange('group_id')}
                                helperText={getErrorMessage('group_id')}
                                variant="outlined"
                                fullWidth
                                color="primary"
                                disabled={(page.public === true)}
                                >
                                <MenuItem key={0} value={0}>
                                    All Users
                                </MenuItem>
                                {groups.map(group => (
                                    <MenuItem key={group.id} value={group.id}>
                                        {group.title}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </>
                    }
                </Paper>
                <Paper className={classes.paper}>
                    <Typography variant="h6" className={classes.marginBottom}>
                        Nav Options
                    </Typography>
                    <Typography variant='subtitle2' gutterBottom color="textSecondary">
                        The navigation label.
                    </Typography>
                    <TextField
                        variant='outlined'
                        value={page.nav_label}
                        id="input-nav-label"
                        onChange={handleChange('nav_label')}
                        fullWidth
                        error={isError('nav_label')}
                        helperText={getErrorMessage('nav_label')}
                        placeholder={page.title != "" ? page.title : 'My Page'}
                        required
                    />
                </Paper>
                <Paper className={classes.paper}>
                    {(page.id > 0 && page.updated_at) &&
                        <>
                            <Alert className={classes.alert} severity={severity}>
                                {status} Page
                            </Alert>
                            <Typography
                                color="primary"
                                variant="subtitle2"
                                className={classes.marginBottom}
                                >
                                <span>
                                    {'Last Updated: ' + DateTime.fromISO(page.updated_at).toLocaleString(DateTime.DATETIME_MED)}
                                </span>
                                <br/>
                                <span>
                                    {publishText}
                                </span>
                            </Typography>
                        </>
                    }
                    <ButtonGroup
                        variant="contained"
                        color="primary"
                        ref={anchorRef}
                        aria-label="Publish Page"
                        className={classes.buttonGroup}
                        disabled={loading}
                        >
                        <Button className={classes.buttonExpand} onClick={postPage}>
                            {publishButton}
                        </Button>
                        <Button
                            color="primary"
                            size="small"
                            aria-controls={publishOptionsOpen ? 'publish-options-menu' : undefined}
                            aria-expanded={publishOptionsOpen ? 'true' : undefined}
                            aria-label="Select Publish Option"
                            aria-haspopup="menu"
                            onClick={handleToggle}
                            >
                            <ArrowDropDownIcon />
                        </Button>
                    </ButtonGroup>
                    <Popper
                        open={publishOptionsOpen}
                        anchorEl={anchorRef.current}
                        role={undefined}
                        transition
                        placement="bottom-end"
                        >
                        {({ TransitionProps }) => (
                            <Grow
                                {...TransitionProps}
                                style={{
                                    transformOrigin: 'center top',
                                }}
                                >
                                <Paper>
                                    <ClickAwayListener onClickAway={handleClose}>
                                        <MenuList id="publish-options-menu">
                                            {publishOptions[status].map((option, index) => (
                                                <MenuItem
                                                    key={index}
                                                    selected={option === publishButton}
                                                    onClick={() => handlePublishOption(option)}
                                                    >
                                                    {option}
                                                </MenuItem>
                                            ))}
                                        </MenuList>
                                    </ClickAwayListener>
                                </Paper>
                            </Grow>
                        )}
                    </Popper>
                    <Collapse in={publishButton === 'Publish Later' || status === 'scheduled'}>
                        <DateTimePicker
                            label="Publish Date"
                            inputVariant="outlined"
                            value={page.published_at}
                            onChange={handleChange('published_at')}
                            variant="outlined"
                            TextFieldComponent={renderDateInput}
                            minutesStep={5}
                            className={classes.marginTop}
                            disablePast
                            clearable
                        />
                    </Collapse>
                    <Button
                        className={classes.marginTop}
                        variant="outlined"
                        fullWidth
                        component={RouterLink}
                        to="/admin/pages"
                        >
                        Back to Pages
                    </Button>
                </Paper>
            </div>
            <Popover
                open={Boolean(colorPickerAnchorEl)}
                anchorEl={colorPickerAnchorEl}
                onClose={closeColorPicker}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                }}
                >
                <SketchPicker color={ activeColor } onChangeComplete={ handlePickedColor } />
            </Popover>
            <SelectFileDialog
                open={selectFileDialogOpen}
                onSelect={closeSelectFileDialogAndAddBlock}
                onClose={closeSelectFileDialog}
                type={selectFileType}
            />
        </div>
    )
}
